import {
    AlertOutlined,
    CloudUploadOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    MailOutlined,
    MessageOutlined,
    RobotOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { type RouterOutputs, trpcOptions } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { formatFileSize } from '@shared/utils/core/file';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Row, Typography } from 'antd';
import { BarChart3 } from 'lucide-react';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/monitoring/statistics')({
    staticData: {
        metadata: {
            title: 'Statistics',
        },
        ui: {
            icon: BarChart3,
            title: 'Organisation statistics',
            subtitle: 'View system statistics and metrics',
            menuDescription: 'View system statistics and metrics',
        },
        modules: ['claim', 'compliance', 'risk'],
        permissions: ['settings:update'],
    },
    component: ConfigurationStats,
});

type OrgStats = RouterOutputs['org']['getOrgStats'];

interface StatCard {
    key: string;
    icon: ReactNode;
    title: string;
    items: { label: string; value: (stats: OrgStats) => ReactNode }[];
}

const STAT_CARDS: StatCard[] = [
    {
        key: 'users',
        icon: <UserOutlined />,
        title: 'User Statistics',
        items: [
            { label: 'Total Users', value: (s) => s.users.totalActiveUsers },
            { label: 'Users Last 24 Hours', value: (s) => s.users.userLoggedInLast24Hours },
            { label: 'Users Last 7 Days', value: (s) => s.users.userLoggedInLast7Days },
            { label: 'Users Last 30 Days', value: (s) => s.users.userLoggedInLast30Days },
        ],
    },
    {
        key: 'complaints',
        icon: <ExclamationCircleOutlined />,
        title: 'Complaints Statistics',
        items: [
            { label: 'Total Complaints', value: (s) => s.complaints.totalComplaints },
            { label: 'Complaints Last 24 Hours', value: (s) => s.complaints.complaintsLast24Hours },
            { label: 'Complaints Last 7 Days', value: (s) => s.complaints.complaintsLast7Days },
            { label: 'Complaints Last 30 Days', value: (s) => s.complaints.complaintsLast30Days },
        ],
    },
    {
        key: 'incidents',
        icon: <AlertOutlined />,
        title: 'Incident Statistics',
        items: [
            { label: 'Total Incidents', value: (s) => s.incidents.totalIncidents },
            { label: 'Incidents Last 24 Hours', value: (s) => s.incidents.incidentsLast24Hours },
            { label: 'Incidents Last 7 Days', value: (s) => s.incidents.incidentsLast7Days },
            { label: 'Incidents Last 30 Days', value: (s) => s.incidents.incidentsLast30Days },
        ],
    },
    {
        key: 'claims',
        icon: <FileTextOutlined />,
        title: 'Claims Statistics',
        items: [
            { label: 'Total Claims', value: (s) => s.claims.totalClaims },
            { label: 'Claims Last 24 Hours', value: (s) => s.claims.claimsLast24Hours },
            { label: 'Claims Last 7 Days', value: (s) => s.claims.claimsLast7Days },
            { label: 'Claims Last 30 Days', value: (s) => s.claims.claimsLast30Days },
        ],
    },
    {
        key: 'files',
        icon: <CloudUploadOutlined />,
        title: 'File Storage Statistics',
        items: [
            { label: 'Total Storage Used', value: (s) => formatFileSize(s.files.totalFileSize) },
            { label: 'Last 24 Hours', value: (s) => formatFileSize(s.files.fileSizeLast24Hours) },
            { label: 'Last 7 Days', value: (s) => formatFileSize(s.files.fileSizeLast7Days) },
            { label: 'Last 30 Days', value: (s) => formatFileSize(s.files.fileSizeLast30Days) },
        ],
    },
    {
        key: 'emails',
        icon: <MailOutlined />,
        title: 'Email Statistics',
        items: [
            { label: 'Total Emails Sent', value: (s) => s.emails.totalEmailsSent },
            { label: 'Emails Last 24 Hours', value: (s) => s.emails.emailsSentLast24Hours },
            { label: 'Emails Last 7 Days', value: (s) => s.emails.emailsSentLast7Days },
            { label: 'Emails Last 30 Days', value: (s) => s.emails.emailsSentLast30Days },
        ],
    },
    {
        key: 'ai',
        icon: <RobotOutlined />,
        title: 'AI Statistics',
        items: [
            { label: 'Total AI Tokens Used', value: (s) => s.ai.totalTokensUsed },
            { label: 'AI Tokens Used Last 24 Hours', value: (s) => s.ai.tokensUsedLast24Hours },
            { label: 'AI Tokens Used Last 7 Days', value: (s) => s.ai.tokensUsedLast7Days },
            { label: 'AI Tokens Used Last 30 Days', value: (s) => s.ai.tokensUsedLast30Days },
        ],
    },
    {
        key: 'sms',
        icon: <MessageOutlined />,
        title: 'SMS Statistics',
        items: [
            { label: 'Total SMS Sent', value: (s) => s.sms.totalSmsSent },
            { label: 'SMS Last 24 Hours', value: (s) => s.sms.smsSentLast24Hours },
            { label: 'SMS Last 7 Days', value: (s) => s.sms.smsSentLast7Days },
            { label: 'SMS Last 30 Days', value: (s) => s.sms.smsSentLast30Days },
        ],
    },
];

const SPAN_CONFIG = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 6,
};

function ConfigurationStats() {
    const {
        data: systemStats,
        error,
        isFetching,
        refetch,
    } = useQuery(trpcOptions.org.getOrgStats.queryOptions(undefined, { meta: { errorSurface: 'inline' } }));
    // A failed first load has nothing to show, so the error replaces the cards rather than leaving
    // them to render as zeros. A failed refresh keeps the cached figures under a compact banner.
    const blocked = hasBlockingQueryError(error, systemStats);

    return (
        <PageController route={Route}>
            {error && (
                <InlineQueryError
                    error={error}
                    title="Couldn't load organisation statistics"
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    description={blocked ? undefined : 'Showing the last available data. Try again to refresh.'}
                    compact={!blocked}
                    className="mb-4"
                />
            )}
            {!blocked && (
                <Row gutter={[16, 16]}>
                    {STAT_CARDS.map((card) => (
                        <Col key={card.key} {...SPAN_CONFIG}>
                            <Card title={<CardTitle icon={card.icon} title={card.title} />} className="h-full">
                                {systemStats ? (
                                    <div className="space-y-4">
                                        {card.items.map((item) => (
                                            <div key={item.label}>
                                                <Typography.Text type="secondary">{item.label}</Typography.Text>
                                                <div className="font-semibold text-lg">{item.value(systemStats)}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex justify-center py-8">
                                        <Loading size="large" />
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </PageController>
    );
}
